// hooks/useRequireAuth.ts — signed-out write-action gate (mirrors legacy auth-gate.js requireAuth).
//
// Returns requireAuth(label): true if signed in; else toasts "Sign in to <label>",
// routes to /signin?next=<current-path>, and returns false.
//
// 1.27.70: inside a surface that provides a write lock (the Videos pages, see writeGate.ts), a
// signed-in member without two-factor sign-in is also stopped, with the surface's reminder
// instead of a sign-in redirect. Reporting and appeals are never locked.

import { useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from './useSession';
import { ALWAYS_ALLOWED, WriteGateContext } from './writeGate';
import { useToast } from '@/ui';

/** Returns requireAuth(label): true if signed in; else toasts "Sign in to <label>", routes to /signin?next, returns false. */
export function useRequireAuth(): (label: string) => boolean {
  const { isAuthenticated } = useSession();
  const gate = useContext(WriteGateContext);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  return useCallback(
    (label: string) => {
      if (isAuthenticated) {
        if (gate?.locked && !ALWAYS_ALLOWED.has(label)) {
          gate.remind();
          return false;
        }
        return true;
      }
      toast(`Sign in to ${label}`);
      navigate(`/signin?next=${encodeURIComponent(location.pathname + location.search)}`);
      return false;
    },
    [isAuthenticated, gate, navigate, location, toast],
  );
}
